import React from 'react';
import {Button, Divider, Stack, capitalize} from '@mui/material';

import {translations} from 'presentation/translations-new';
import {StyledCard} from 'presentation/components';
import {useAccountInformation} from 'presentation/pages/private/hooks';
import {formatText, getFlowTypeByBookingType} from 'presentation/utils';
import Icon from 'presentation/icons';
import {useNavigate} from 'react-router-dom';
import {MY_APPOINTMENT_DETAILS_PATH} from 'presentation/modules/myAppointments/constants/appointmentDetailsPath';
import {ContentListProps} from 'presentation/components/StyledCard/types';
import {BookingTypeEnum, TELEMEDICINE_INFO} from '../../constants';

import {DuplicityBookedCardProps} from './types';
import {useBookingTheme} from 'presentation/modules/booking/shared/hooks/useBookingTheme';

const {at, showDetails} = translations['pt-br'].commons;

export function DuplicityBookedCard({
	scheduledBooking,
	maxWidth = 370,
	showGoDetails,
	isScheduleScript,
}: DuplicityBookedCardProps) {
	const navigate = useNavigate();
	const {userName} = useAccountInformation();
	const {color, variant, iconName} = useBookingTheme({
		flowType: getFlowTypeByBookingType(scheduledBooking.bookingType),
	});

	const isTelehealth = scheduledBooking.bookingType === BookingTypeEnum.TELEHEALTH;

	function handleGoToDetails() {
		navigate(
			MY_APPOINTMENT_DETAILS_PATH[scheduledBooking.bookingType].replace(
				':id',
				scheduledBooking.bookingNumber,
			),
		);
	}

	const CONTENT_LIST: ContentListProps[] = [
		{
			primaryText: formatText(userName),
			iconName: 'account-circle-filled',
		},
		{
			primaryText: formatText(scheduledBooking.providerName),
			secondaryText: formatText(scheduledBooking.specialtyName),
			secondaryTextColor: 'text.secondary',
			iconName: 'medical-services-filled',
		},
		{
			primaryText: isTelehealth
				? formatText(TELEMEDICINE_INFO.name)
				: formatText(scheduledBooking.clinicName),
			iconName: isTelehealth ? 'remember-me-filled' : 'location-on-filled',
		},
	];

	return (
		<StyledCard.Container
			$borderTop
			$borderColor={color}
			elevation={1}
			sx={{maxWidth, width: '100%'}}>
			<StyledCard.Header
				iconProps={{
					variant,
					customIconName: isTelehealth ? 'remember-me-filled' : iconName,
				}}
				title={capitalize(scheduledBooking.formattedDateWithWeekday || '')}
				subtitle={`${at} ${scheduledBooking.hour}`}
			/>
			<Divider />

			<StyledCard.Content list={CONTENT_LIST} sx={{flexDirection: 'column'}} />
			{showGoDetails && !isScheduleScript ? (
				<Stack alignItems={'end'}>
					<Button
						variant="text"
						endIcon={<Icon name="arrow-right" />}
						onClick={handleGoToDetails}>
						{showDetails}
					</Button>
				</Stack>
			) : null}
		</StyledCard.Container>
	);
}
